import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Sparkles, RotateCcw, Loader2, CheckCircle2, Database, Mail, BarChart3, PanelRightOpen, PanelRightClose, Settings, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AnalyticsSidebar from '../components/chat/AnalyticsSidebar';
import OnboardingMessage, { OnboardingBanner } from '../components/chat/OnboardingMessage';
import PinnedApprovals from '../components/chat/PinnedApprovals';
import DeepSearchProgress from '../components/chat/DeepSearchProgress';
import SmartEmailSettings from '../components/email/SmartEmailSettings';
import ChatHistoryDropdown from '../components/chat/ChatHistoryDropdown';
import { useChatStore } from '../store/chatStore';
import { endpoints } from '../api/api';

const API_BASE_URL = (process.env.REACT_APP_PYTHON_API_URL || "").replace("http://", "https://");

export default function AiAssistant({ darkMode, user }) {
    const {
        currentSessionId,
        messages: storeMessages,
        loadMessages,
        setCurrentSession,
        addMessageToStore
    } = useChatStore();

    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [isIngesting, setIsIngesting] = useState(false);
    const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [searchProgress, setSearchProgress] = useState({ steps: [], currentStep: null });

    // New onboarding state
    const [onboardingState, setOnboardingState] = useState(null);
    const [onboardingMessage, setOnboardingMessage] = useState(null);
    const [ingestionProgress, setIngestionProgress] = useState(null);

    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Sync messages from store when session changes
    useEffect(() => {
        if (currentSessionId && storeMessages[currentSessionId]) {
            setMessages(storeMessages[currentSessionId]);
        } else if (!currentSessionId) {
            // New chat cleared state
        }
    }, [currentSessionId, storeMessages]);

    // Check onboarding status on mount
    useEffect(() => {
        if (!user || hasCheckedOnboarding) return;

        const userId = user._id || user.id;
        const token = localStorage.getItem('token');

        const checkOnboarding = async () => {
            try {
                // Check email status first
                const statusRes = await fetch(`${API_BASE_URL}/api/v1/emails/status?user_id=${userId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await statusRes.json();

                // Case 1: Already has emails - fully onboarded
                if (data.email_count > 0) {
                    // If we have history loaded, don't overwrite it with welcome message if session exists
                    if (!currentSessionId) {
                        setMessages([{
                            role: 'assistant',
                            content: `Welcome back! 👋 I have access to **${data.email_count} emails** and **${data.node_count || 0} contacts** in your Knowledge Graph. How can I help you today?`
                        }]);
                    }
                    setHasCheckedOnboarding(true);
                    return;
                }

                // Case 2: Currently ingesting
                if (data.is_ingesting) {
                    setIsIngesting(true);
                    setMessages([{
                        role: 'assistant',
                        content: '📧 I am currently importing your emails into the Knowledge Graph. Please wait...',
                        isProgress: true
                    }]);
                    pollProgress(userId, token);
                    setHasCheckedOnboarding(true);
                    return;
                }

                // Case 3: Gmail is connected (has credentials) but no emails yet - auto-start ingestion
                if (data.gmail_connected) {
                    setMessages([{
                        role: 'assistant',
                        content: '✅ Gmail connected! Starting to import your emails now...',
                        isProgress: true
                    }]);
                    triggerIngestion();
                    setHasCheckedOnboarding(true);
                    return;
                }

                // Case 4: Gmail not connected
                if (!currentSessionId) {
                    setMessages([{
                        role: 'assistant',
                        content: `👋 Hi! I'm Capy, your AI executive assistant.

To help you manage emails, relationships, and meetings, I need to connect to your Gmail first.

**Please go to the Integrations tab** in the sidebar and connect your Gmail account. I'll automatically start importing your emails once connected!`
                    }]);
                }
                setHasCheckedOnboarding(true);

            } catch (e) {
                console.error('Onboarding check failed:', e);
                if (!currentSessionId) {
                    setMessages([{
                        role: 'assistant',
                        content: 'Hello! I am Capy, your AI agent. How can I help you today?'
                    }]);
                }
                setHasCheckedOnboarding(true);
            }
        };

        checkOnboarding();
    }, [user, hasCheckedOnboarding, currentSessionId]);


    const pollProgress = async (userId, token) => {
        const poll = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/v1/emails/status?user_id=${userId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();

                if (data.is_ingesting) {
                    const logsText = data.logs?.length > 0
                        ? data.logs.map(log => `\`${log}\``).join('\n')
                        : '`Connecting to Gmail API...`';

                    setMessages(prev => {
                        const newMessages = [...prev];
                        const progressIdx = newMessages.findIndex(m => m.isProgress);
                        if (progressIdx >= 0) {
                            newMessages[progressIdx] = {
                                ...newMessages[progressIdx],
                                content: `**📧 Importing Emails**

${logsText}

\`Currently: ${data.email_count} emails | ${data.node_count} people | ${data.relationship_count} connections\``,
                                isTerminal: true
                            };
                        }
                        return newMessages;
                    });
                    setTimeout(poll, 2000);
                } else {
                    setIsIngesting(false);
                    setMessages(prev => {
                        const newMessages = prev.filter(m => !m.isProgress);
                        return [...newMessages, {
                            role: 'assistant',
                            content: `✅ **Knowledge Graph Ready!**

I've imported **${data.email_count} emails** and built your Knowledge Graph. How can I help you today?`
                        }];
                    });
                }
            } catch (e) {
                console.error('Poll failed:', e);
                setIsIngesting(false);
            }
        };
        poll();
    };

    const triggerIngestion = async () => {
        const userId = user._id || user.id;
        const token = localStorage.getItem('token');

        setIsIngesting(true);
        setMessages(prev => [...prev, {
            role: 'assistant',
            content: '📧 Starting email import... This may take a few minutes.',
            isProgress: true
        }]);

        try {
            await fetch(`${API_BASE_URL}/api/v1/emails/ingest`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ user_id: userId })
            });
            pollProgress(userId, token);
        } catch (e) {
            console.error('Ingestion failed:', e);
            setIsIngesting(false);
            setMessages(prev => [...prev.filter(m => !m.isProgress), {
                role: 'assistant',
                content: '❌ Failed to start email import. Please check your Gmail connection in Integrations.'
            }]);
        }
    };

    const handleOnboardingAction = async (action, data = null) => {
        const userId = user._id || user.id;
        try {
            switch (action) {
                case 'start':
                    setOnboardingMessage({
                        type: 'connect',
                        step: 'connect_gmail',
                        title: 'Step 1: Connect Your Accounts',
                        content: 'Please connect Gmail in the **Integrations** tab first. I\'ll automatically detect when it\'s done and start importing your emails.\n\nClick the button below to go to Integrations:',
                        buttons: [
                            { label: 'Go to Integrations →', action: 'goto_integrations', primary: true },
                            { label: 'Skip Setup', action: 'skip' }
                        ],
                        is_blocking: false
                    });
                    break;
                case 'connect_gmail':
                case 'connect_slack':
                case 'connect_calendar':
                case 'goto_integrations':
                    setOnboardingMessage(null);
                    setMessages(prev => [...prev, {
                        role: 'assistant',
                        content: '👉 Please go to the **Integrations** tab in the sidebar to connect your accounts.'
                    }]);
                    break;
                case 'skip_slack':
                case 'skip_calendar':
                    const skipRes = await endpoints.onboarding.skipStep(userId, action.replace('skip_', 'connect_'));
                    setOnboardingState(skipRes.data.state);
                    setOnboardingMessage(skipRes.data.message);
                    break;

                case 'skip':
                    const skipAllRes = await endpoints.onboarding.skip(userId);
                    setOnboardingState(skipAllRes.data.state);
                    setOnboardingMessage(null);
                    setMessages(prev => [...prev, {
                        role: 'assistant',
                        content: '⚡ Running in limited mode.'
                    }]);
                    break;

                case 'answer_role':
                case 'answer_hours':
                case 'answer_style':
                    const answer = Array.isArray(data) ? data[0] : data;
                    const question = action.replace('answer_', '');
                    const answerRes = await endpoints.onboarding.answer(userId, question, answer);
                    setOnboardingState(answerRes.data.state);
                    setOnboardingMessage(answerRes.data.message);
                    break;

                case 'answer_vips':
                case 'answer_priorities':
                    const multiAnswer = Array.isArray(data) ? data : [data];
                    const multiQuestion = action.replace('answer_', '');
                    const multiRes = await endpoints.onboarding.answer(userId, multiQuestion, multiAnswer);
                    setOnboardingState(multiRes.data.state);
                    setOnboardingMessage(multiRes.data.message);
                    break;

                case 'complete':
                    const completeRes = await endpoints.onboarding.complete(userId);
                    setOnboardingState(completeRes.data.state);
                    setOnboardingMessage(null);
                    setMessages(prev => [...prev, {
                        role: 'assistant',
                        content: '🎉 You\'re all set! Try asking me something!'
                    }]);
                    break;

                case 'resume':
                    const resumeRes = await endpoints.onboarding.getStatus(userId);
                    setOnboardingState(resumeRes.data.state);
                    setOnboardingMessage(resumeRes.data.message);
                    break;

                default:
                    console.log('Unknown onboarding action:', action);
            }
        } catch (error) {
            console.error(error);
        }
    };

    // Kept triggerIngestionWithProgress for completeness if used
    const triggerIngestionWithProgress = async () => {
        // ... (Keep existing)
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        const userMsg = { role: 'user', content: input };
        const userInput = input.toLowerCase().trim();

        // Optimistic UI update
        setMessages(prev => [...prev, userMsg]);
        setInput('');

        // Add to Store IF we have a session
        if (currentSessionId) {
            addMessageToStore(currentSessionId, userMsg);
        }

        // Check for onboarding responses 
        const lastAssistantMsg = messages.filter(m => m.role === 'assistant').pop();
        if (lastAssistantMsg?.isOnboarding) {
            if (userInput === 'yes' || userInput === 'start') {
                triggerIngestion();
                return;
            } else if (userInput === 'skip' || userInput === 'no') {
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: 'No problem! You can still ask me questions, but I won\'t have context from your emails.'
                }]);
                return;
            }
        }

        if (userInput.includes('import') && userInput.includes('email')) {
            triggerIngestion();
            return;
        }

        setLoading(true);
        setSearchProgress({ steps: [], currentStep: null });

        try {
            const HISTORY_LIMIT = 10;
            const history = messages.slice(-HISTORY_LIMIT).map(m => ({
                role: m.role,
                content: m.content
            }));

            const token = localStorage.getItem('token');
            const userId = user._id || user.id;

            // Use streaming endpoint with session_id
            const res = await fetch(`${API_BASE_URL}/api/v1/chat/message/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    user_id: userId,
                    message: userMsg.content,
                    session_id: currentSessionId, // Pass current session
                    history: history
                })
            });

            if (!res.ok) {
                setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I encountered an error." }]);
                setLoading(false);
                return;
            }

            // Handle SSE stream
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let activeSessionId = currentSessionId;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const event = JSON.parse(line.slice(6));

                            if (event.type === 'session_id') {
                                // New session created or confirmed
                                if (event.session_id !== activeSessionId) {
                                    activeSessionId = event.session_id;
                                    setCurrentSession(activeSessionId);
                                    // Make sure we update store with user message for THIS session now that we know ID
                                    loadMessages(activeSessionId); // Refresh store
                                }
                            } else if (event.type === 'searching' || event.type === 'thinking') {
                                setSearchProgress(prev => ({
                                    steps: event.type === 'complete' ? [...prev.steps, prev.currentStep].filter(Boolean) : prev.steps,
                                    currentStep: { type: event.step, count: event.count, message: event.message }
                                }));
                            } else if (event.type === 'complete') {
                                setSearchProgress(prev => ({
                                    steps: [...prev.steps, prev.currentStep].filter(Boolean),
                                    currentStep: null
                                }));
                            } else if (event.type === 'response') {
                                setSearchProgress({ steps: [], currentStep: null });
                                const assistantMsg = { role: 'assistant', content: event.content };
                                setMessages(prev => [...prev, assistantMsg]);
                                // Store update
                                if (activeSessionId) addMessageToStore(activeSessionId, assistantMsg);
                            } else if (event.type === 'error') {
                                setSearchProgress({ steps: [], currentStep: null });
                                let errorMsg = `Error: ${event.message}`;
                                if (event.message && event.message.includes('Daily limit reached')) {
                                    // Ensure your renderer treats this as HTML, not plain text
                                    errorMsg = `🛑 **Daily Limit Reached**

You have exhausted your daily quota of 10 queries.

🚀 **[Go Pro for Unlimited Access](https://hicapy.com/pro)**

---
🔐 **Enterprise & Secret Features**
We are working on limited secret features! For enterprise deals or early access, schedule a meeting with the founders:

📅 **[Schedule with Subhradeep Dey](https://hicapy.com/schedule/Subhradeep_Dey)**
📅 **[Schedule with Snehal Pal](https://hicapy.com/schedule/Snehal_Pal)**`;
                                }
                                setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
                            }
                        } catch (e) {
                            console.error('Failed to parse SSE event:', e);
                        }
                    }
                }
            }

        } catch (error) {
            console.error(error);
            setSearchProgress({ steps: [], currentStep: null });

            // Graceful error handling
            let errorMsg = "Network error. Please check your connection.";
            if (error.message && error.message.includes('Daily limit reached')) {
                errorMsg = `🛑 **Daily Limit Reached**\n\nYou have exhausted your daily quota of 10 queries.\n\n🚀 **Go Pro** for unlimited access.\n\n🔐 **Enterprise & Secret Features**\nWe are working on limited secret features! For enterprise deals or early access, schedule a meeting with the founders:\n\n📅 [Schedule with Subhradeep Dey](https://hicapy.com/schedule/Subhradeep_Dey)\n📅 [Schedule with Snehal Pal](https://hicapy.com/schedule/Snehal_Pal)`;
            } else if (error.message) {
                // Try to parse if it's a JSON string error from our backend
                try {
                    const parsed = JSON.parse(error.message);
                    if (parsed.detail) errorMsg = `Error: ${parsed.detail}`;
                } catch (e) {
                    // If it's just a string, use it (truncate if too long)
                    if (error.message.length < 200) errorMsg = `Error: ${error.message}`;
                }
            }

            setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`flex flex-col h-full min-h-0 ${darkMode ? 'bg-[#0F172A] text-white' : 'bg-slate-50 text-slate-900'}`}>

            {/* HEADER with Dropdown */}
            <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-white/5 bg-slate-900/50' : 'border-slate-200 bg-white'}`}>
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-blue-600/20 text-blue-500">
                        <Sparkles size={20} />
                    </div>
                    {/* Chat History Dropdown Replaces Title */}
                    <div className="flex flex-col">
                        <span className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Assistant</span>
                        <ChatHistoryDropdown userId={user?._id || user?.id} />
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Quota Indicator */}
                    <QuotaIndicator user={user} />

                    <button
                        onClick={() => setShowAnalytics(!showAnalytics)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${showAnalytics
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                            : darkMode ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
                            }`}
                        title="Toggle Analytics"
                    >
                        {showAnalytics ? <PanelRightClose size={18} /> : <BarChart3 size={18} />}
                        <span className="hidden sm:inline text-sm font-medium">Analytics</span>
                    </button>

                    <button
                        onClick={() => setShowSettings(true)}
                        className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                        title="Email Settings"
                    >
                        <Settings size={18} />
                    </button>

                    <button
                        onClick={() => {
                            // If we just want to clear current view
                            setMessages([{ role: 'assistant', content: 'Chat cleared locally.' }]);
                            setCurrentSession(null); // Deselect session
                        }}
                        className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                        title="New Chat / Reset"
                    >
                        <RotateCcw size={18} />
                    </button>
                </div>
            </div>



            <div className="flex flex-1 min-h-0 overflow-hidden relative">
                {/* MESSAGES */}
                <div className="flex-1 flex flex-col min-w-0">
                    {/* Onboarding Banner */}
                    {onboardingState && !onboardingState.onboarding_complete && !onboardingState.skipped && !onboardingMessage && (
                        <OnboardingBanner
                            state={onboardingState}
                            darkMode={darkMode}
                            onResume={() => handleOnboardingAction('resume')}
                        />
                    )}

                    {/* Pinned Approvals */}
                    <PinnedApprovals darkMode={darkMode} user={user} />

                    <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 space-y-6">
                        {/* Onboarding Message */}
                        {onboardingMessage && (
                            <OnboardingMessage
                                message={onboardingMessage}
                                darkMode={darkMode}
                                onAction={handleOnboardingAction}
                                progress={ingestionProgress}
                            />
                        )}

                        {messages.map((msg, idx) => (
                            <div key={idx} className={`flex w-full ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`flex max-w-[80%] md:max-w-[70%] gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>

                                    {/* Avatar */}
                                    <div className={`
                                w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-1
                                ${msg.role === 'user'
                                            ? 'bg-slate-200 text-slate-600'
                                            : msg.isProgress
                                                ? 'bg-amber-500/20 text-amber-500'
                                                : 'bg-blue-600 text-white'}
                            `}>
                                        {msg.role === 'user' ? <User size={16} /> : msg.isProgress ? <Loader2 size={16} className="animate-spin" /> : <Bot size={16} />}
                                    </div>

                                    {/* Bubble */}
                                    <div className={`
                                p-4 rounded-2xl text-sm leading-relaxed shadow-sm
                                ${msg.role === 'user'
                                            ? 'bg-blue-600 text-white rounded-tr-none'
                                            : (darkMode ? 'bg-slate-800 text-slate-200 border border-white/5 rounded-tl-none' : 'bg-white text-slate-700 border border-slate-200 rounded-tl-none')}
                            `}>
                                        {(msg.content || '').split('\n').map((line, i) => (
                                            <p key={i} className={i > 0 ? 'mt-2' : ''}>
                                                {line.split('**').map((part, j) =>
                                                    j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                                                )}
                                            </p>
                                        ))}
                                    </div>

                                </div>
                            </div>
                        ))}

                        {/* Deep Search Progress */}
                        {loading && (searchProgress.currentStep || searchProgress.steps.length > 0) && (
                            <div className="flex justify-start w-full">
                                <div className="flex max-w-[80%] gap-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0 mt-1">
                                        <Bot size={16} />
                                    </div>
                                    <DeepSearchProgress
                                        steps={searchProgress.steps}
                                        currentStep={searchProgress.currentStep}
                                        darkMode={darkMode}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Simple loading dots */}
                        {loading && !searchProgress.currentStep && searchProgress.steps.length === 0 && (
                            <div className="flex justify-start w-full">
                                <div className="flex max-w-[80%] gap-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0 mt-1">
                                        <Bot size={16} />
                                    </div>
                                    <div className={`p-4 rounded-2xl rounded-tl-none ${darkMode ? 'bg-slate-800 border border-white/5' : 'bg-white border-slate-200'}`}>
                                        <div className="flex gap-1">
                                            <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce"></span>
                                            <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.15s]"></span>
                                            <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.3s]"></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* INPUT */}
                    <div className={`shrink-0 p-4 border-t ${darkMode ? 'border-white/5 bg-slate-900' : 'border-slate-200 bg-white'}`}>
                        <form onSubmit={handleSend} className="max-w-4xl mx-auto relative">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={isIngesting ? "Please wait while importing emails..." : "Ask Capy anything..."}
                                disabled={isIngesting}
                                className={`
                            w-full pl-6 pr-14 py-4 rounded-full text-sm outline-none transition-all shadow-sm
                            ${darkMode
                                        ? 'bg-slate-800 text-white border border-white/10 focus:border-blue-500'
                                        : 'bg-slate-50 text-slate-900 border border-slate-200 focus:border-blue-500'}
                            ${isIngesting ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                            />
                            <button
                                type="submit"
                                disabled={!input.trim() || loading || isIngesting}
                                className={`
                            absolute right-2 top-2 p-2 rounded-full transition-all
                            ${!input.trim() || loading || isIngesting
                                        ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'}
                        `}
                            >
                                <Send size={18} />
                            </button>
                        </form>
                        <p className={`text-center text-[10px] mt-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Capy can make mistakes. Verify important information.
                        </p>
                    </div>
                </div>

                {/* RIGHT ANALYTICS SIDEBAR */}
                <AnimatePresence>
                    {showAnalytics && (
                        <motion.div
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 320, opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            className="h-full shrink-0 hidden md:block z-10"
                        >
                            <AnalyticsSidebar darkMode={darkMode} onClose={() => setShowAnalytics(false)} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Settings Modal */}
            <AnimatePresence>
                {showSettings && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                        onClick={() => setShowSettings(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className={`max-w-lg w-full mx-4 rounded-2xl shadow-2xl ${darkMode ? 'bg-slate-800' : 'bg-white'}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-white/10' : 'border-slate-200'}`}>
                                <h3 className="font-semibold">Email & Approval Settings</h3>
                                <button onClick={() => setShowSettings(false)} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10">
                                    <X size={20} />
                                </button>
                            </div>
                            <SmartEmailSettings darkMode={darkMode} onClose={() => setShowSettings(false)} />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

const QuotaIndicator = ({ user }) => {
    const [quota, setQuota] = useState({ count: 0, limit: 10, nextReset: null });
    const [loading, setLoading] = useState(true);

    const fetchQuota = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();

            // Calculate reset time (Midnight UTC)
            const now = new Date();
            const tomorrow = new Date(now);
            tomorrow.setUTCHours(24, 0, 0, 0);

            setQuota({
                count: data.user?.query_count || data.query_count || 0,
                limit: data.user?.query_limit || data.query_limit || 10,
                nextReset: tomorrow
            });
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuota();
        const interval = setInterval(fetchQuota, 30000); // Poll every 30s
        return () => clearInterval(interval);
    }, []);

    const remaining = Math.max(0, quota.limit - quota.count);
    const percentage = (remaining / quota.limit) * 100;
    const isEmpty = remaining === 0;

    // Time until reset
    const getTimeUntilReset = () => {
        if (!quota.nextReset) return "";
        const diff = quota.nextReset - new Date();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}h ${mins}m`;
    };

    return (
        <div className="relative group mr-2 flex items-center">
            <div className="relative w-8 h-8 flex items-center justify-center">
                <svg className="w-full h-full -rotate-90 transform">
                    <circle cx="16" cy="16" r="13" stroke="currentColor" strokeWidth="2.5" fill="none" className="text-slate-200 dark:text-slate-700 opacity-50" />
                    <circle
                        cx="16" cy="16" r="13"
                        stroke="currentColor" strokeWidth="2.5" fill="none"
                        strokeDasharray={81}
                        strokeDashoffset={81 - (81 * percentage) / 100}
                        className={`transition-all duration-500 ${isEmpty ? 'text-red-500' : 'text-blue-500'}`}
                    />
                </svg>
                <span className={`absolute text-[10px] font-bold ${isEmpty ? 'text-red-500' : 'text-slate-500 dark:text-slate-400'}`}>
                    {remaining}
                </span>
            </div>

            {/* Tooltip */}
            <div className="absolute top-full right-0 mt-2 w-48 p-3 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-white/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-xs">
                <div className="flex justify-between font-semibold mb-1">
                    <span>Daily Quota</span>
                    <span className={isEmpty ? 'text-red-500' : 'text-blue-500'}>{remaining}/{quota.limit} left</span>
                </div>
                {isEmpty ? (
                    <div className="text-slate-500 dark:text-slate-400">
                        Limit reached. Resets in <span className="text-white font-medium">{getTimeUntilReset()}</span>
                    </div>
                ) : (
                    <div className="text-slate-400">Resets daily at UTC midnight.</div>
                )}
            </div>
        </div>
    );
};
